import type { ReactNode } from "react";
import React from "react";
import { AppConfigContext } from "../app/appConfig";
import { useAlert } from "../app/notificationToast";
import { useConfirmationModal } from "../app/confirmation";
import { EquipmentModel as EquipmentModelClass } from "../../api/equipment/EquipmentModel";
import { EquipmentAdapter } from "../../api/equipment/EquipmentAdapter";
import type {
  Equipment,
  EquipmentModel as EquipmentModelType,
  EquipmentModelsPaginationRequest,
  EquipmentSearchRequest,
} from "../../api/equipment/Equipment.typed";
import type { PaginatedResponse } from "../../api/zekay/pagination";

interface EquipmentContextType {
  getEquipmentModels: (
    paginationParams?: EquipmentModelsPaginationRequest,
  ) => Promise<PaginatedResponse<EquipmentModelType> | EquipmentModelType[] | undefined>;
  getEquipmentModel: (id: number) => Promise<EquipmentModelType | undefined>;
  createEquipmentModel: (model: EquipmentModelType) => Promise<void>;
  updateEquipmentModel: (model: EquipmentModelType) => Promise<void>;
  deleteEquipmentModel: (id: number) => Promise<void>;
  getEquipments: () => Promise<Equipment[] | undefined>;
  getEquipment: (id: string) => Promise<Equipment | undefined>;
  createEquipment: (e: Equipment) => Promise<void>;
  updateEquipment: (e: Equipment) => Promise<void>;
  deleteEquipment: (id: string) => Promise<void>;
  getEquipmentQRCode: (id: string) => Promise<Blob | undefined>;
  regenerateEquipmentQRCode: (id: string, backgroundColor?: string) => Promise<void>;
  getEquipmentsByModelID: (
    modelId: number,
    page?: number,
    limit?: number,
  ) => Promise<{ equipments: Equipment[]; total: number; pages: number } | undefined>;
  searchEquipmentsByModelID: (
    searchRequest: EquipmentSearchRequest,
  ) => Promise<{ equipments: Equipment[]; total: number; pages: number } | undefined>;
  searchEquipmentsBatch: (
    requests: EquipmentSearchRequest[],
  ) => Promise<Array<{ total: number }> | undefined>;
  exportEquipmentsByModelIDCSV: (modelId: number) => Promise<Blob | undefined>;
}

const EquipmentContext = React.createContext<EquipmentContextType>(null!);

export function EquipmentProvider(props: { children: ReactNode }) {
  const appConfig = React.useContext(AppConfigContext);
  const { showAlert } = useAlert();
  const { showConfirmation } = useConfirmationModal();

  const adapter = React.useMemo(() => {
    if (!appConfig) return null;
    const model = new EquipmentModelClass(appConfig);
    return new EquipmentAdapter(model);
  }, [appConfig]);

  const wrap = React.useCallback(
    async <T,>(fn: () => Promise<T>): Promise<T | undefined> => {
      try {
        return await fn();
      } catch (err) {
        showAlert(err instanceof Error ? err.message : "Erreur", "danger");
        return undefined;
      }
    },
    [showAlert],
  );

  const getEquipmentModels = React.useCallback(
    async (
      paginationParams?: EquipmentModelsPaginationRequest,
    ): Promise<PaginatedResponse<EquipmentModelType> | EquipmentModelType[] | undefined> =>
      adapter ? wrap(() => adapter.getEquipmentModels(paginationParams)) : undefined,
    [adapter, wrap],
  );

  const getEquipmentModel = React.useCallback(
    async (id: number): Promise<EquipmentModelType | undefined> =>
      adapter ? wrap(() => adapter.getEquipmentModel(id)) : undefined,
    [adapter, wrap],
  );

  const createEquipmentModel = React.useCallback(
    async (model: EquipmentModelType): Promise<void> => {
      if (!adapter) return;
      try {
        await adapter.createEquipmentModel(model);
        showAlert("Modèle créé", "success");
      } catch (err) {
        showAlert(err instanceof Error ? err.message : "Erreur", "danger");
      }
    },
    [adapter, showAlert],
  );

  const updateEquipmentModel = React.useCallback(
    async (model: EquipmentModelType): Promise<void> => {
      if (!adapter) return;
      try {
        await adapter.updateEquipmentModel(model);
        showAlert("Modèle mis à jour", "success");
      } catch (err) {
        showAlert(err instanceof Error ? err.message : "Erreur", "danger");
      }
    },
    [adapter, showAlert],
  );

  const deleteEquipmentModel = React.useCallback(
    async (id: number): Promise<void> => {
      if (!adapter) return;
      const ok = await showConfirmation(
        "Supprimer le modèle",
        "Êtes-vous sûr de vouloir supprimer ce modèle d'équipement ?",
      );
      if (!ok) return;
      try {
        await adapter.deleteEquipmentModel(id);
        showAlert("Modèle supprimé", "success");
      } catch (err) {
        showAlert(err instanceof Error ? err.message : "Erreur", "danger");
      }
    },
    [adapter, showAlert, showConfirmation],
  );

  const getEquipments = React.useCallback(
    async (): Promise<Equipment[] | undefined> =>
      adapter ? wrap(() => adapter.getEquipments()) : undefined,
    [adapter, wrap],
  );

  const getEquipment = React.useCallback(
    async (id: string): Promise<Equipment | undefined> =>
      adapter ? wrap(() => adapter.getEquipment(id)) : undefined,
    [adapter, wrap],
  );

  const createEquipment = React.useCallback(
    async (e: Equipment): Promise<void> => {
      if (!adapter) return;
      try {
        await adapter.createEquipment(e);
        showAlert("Équipement créé", "success");
      } catch (err) {
        showAlert(err instanceof Error ? err.message : "Erreur", "danger");
      }
    },
    [adapter, showAlert],
  );

  const updateEquipment = React.useCallback(
    async (e: Equipment): Promise<void> => {
      if (!adapter) return;
      try {
        await adapter.updateEquipment(e);
        showAlert("Équipement mis à jour", "success");
      } catch (err) {
        showAlert(err instanceof Error ? err.message : "Erreur", "danger");
      }
    },
    [adapter, showAlert],
  );

  const deleteEquipment = React.useCallback(
    async (id: string): Promise<void> => {
      if (!adapter) return;
      const ok = await showConfirmation(
        "Supprimer l'équipement",
        "Êtes-vous sûr de vouloir supprimer cet équipement ?",
      );
      if (!ok) return;
      try {
        await adapter.deleteEquipment(id);
        showAlert("Équipement supprimé", "success");
      } catch (err) {
        showAlert(err instanceof Error ? err.message : "Erreur", "danger");
      }
    },
    [adapter, showAlert, showConfirmation],
  );

  const getEquipmentQRCode = React.useCallback(
    async (id: string): Promise<Blob | undefined> =>
      adapter ? wrap(() => adapter.getEquipmentQRCode(id)) : undefined,
    [adapter, wrap],
  );

  const regenerateEquipmentQRCode = React.useCallback(
    async (id: string, backgroundColor?: string): Promise<void> => {
      if (!adapter) return;
      try {
        await adapter.regenerateEquipmentQRCode(id, backgroundColor);
        showAlert("QR Code régénéré", "success");
      } catch (err) {
        showAlert(err instanceof Error ? err.message : "Erreur", "danger");
      }
    },
    [adapter, showAlert],
  );

  const getEquipmentsByModelID = React.useCallback(
    async (
      modelId: number,
      page?: number,
      limit?: number,
    ): Promise<{ equipments: Equipment[]; total: number; pages: number } | undefined> =>
      adapter ? wrap(() => adapter.getEquipmentsByModelID(modelId, page, limit)) : undefined,
    [adapter, wrap],
  );

  const searchEquipmentsByModelID = React.useCallback(
    async (
      searchRequest: EquipmentSearchRequest,
    ): Promise<{ equipments: Equipment[]; total: number; pages: number } | undefined> =>
      adapter ? wrap(() => adapter.searchEquipmentsByModelID(searchRequest)) : undefined,
    [adapter, wrap],
  );

  const searchEquipmentsBatch = React.useCallback(
    async (requests: EquipmentSearchRequest[]): Promise<Array<{ total: number }> | undefined> =>
      adapter ? wrap(() => adapter.searchEquipmentsBatch(requests)) : undefined,
    [adapter, wrap],
  );

  const exportEquipmentsByModelIDCSV = React.useCallback(
    async (modelId: number): Promise<Blob | undefined> =>
      adapter ? wrap(() => adapter.exportEquipmentsByModelIDCSV(modelId)) : undefined,
    [adapter, wrap],
  );

  const value = React.useMemo<EquipmentContextType>(
    () => ({
      getEquipmentModels,
      getEquipmentModel,
      createEquipmentModel,
      updateEquipmentModel,
      deleteEquipmentModel,
      getEquipments,
      getEquipment,
      createEquipment,
      updateEquipment,
      deleteEquipment,
      getEquipmentQRCode,
      regenerateEquipmentQRCode,
      getEquipmentsByModelID,
      searchEquipmentsByModelID,
      searchEquipmentsBatch,
      exportEquipmentsByModelIDCSV,
    }),
    [
      getEquipmentModels,
      getEquipmentModel,
      createEquipmentModel,
      updateEquipmentModel,
      deleteEquipmentModel,
      getEquipments,
      getEquipment,
      createEquipment,
      updateEquipment,
      deleteEquipment,
      getEquipmentQRCode,
      regenerateEquipmentQRCode,
      getEquipmentsByModelID,
      searchEquipmentsByModelID,
      searchEquipmentsBatch,
      exportEquipmentsByModelIDCSV,
    ],
  );

  return <EquipmentContext.Provider value={value}>{props.children}</EquipmentContext.Provider>;
}

export function useEquipment(): EquipmentContextType {
  const ctx = React.useContext(EquipmentContext);
  if (!ctx) throw new Error("useEquipment must be used within EquipmentProvider");
  return ctx;
}
